/**
 * W4 (field test 2026-10-16) — THE END-OF-DAY DECISION PROMPT. Non-blocking, persistent, escalating (D-5).
 *
 * The source of truth is the server: open `rollover_conversions` rows (state 'proposed') raised by the lane walk or
 * requested by a technician. This panel only READS them and records a person's choice through the
 * `rollover-decision` Edge function (which calls decide_rollover_conversion with the person's own JWT).
 *
 *   - Options in the D-4 order, NOTHING preselected: 1 Reassign · 2 Approve rollover · 3 Authorize overtime ·
 *     4 Reschedule · 5 Leave unresolved. Consequences and affected promises are shown; disabled options say why.
 *   - Never a modal, never on a technician's timer: a banner that grows more urgent as the end of day nears.
 *   - Shown to approvers only (`can_i_decide_rollover` — the server's D-1 predicate; DISPATCHER not admitted).
 *   - Catalyst may only recommend: a recommendation, when present, is labelled as such and never selects anything.
 *   - No writes per minute: a 60-second READ refresh; writes happen only when the person confirms.
 *   - B-W3: "Authorize overtime" is shown unavailable (with the reason) to a person the server will refuse — the answer
 *     is the server's own (`can_i_authorize_overtime` → can_authorize_overtime); who may authorize is unchanged.
 *   - Server refusal codes are shown in words (rolloverDecisionMessages), never as a raw code.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScheduleSignalRefresh } from '@/hooks/useScheduleSignalRefresh';
import { useLatestResponseGuard } from '@/hooks/useLatestResponseGuard';
import { getScheduleDock } from '@/components/scheduling/scheduleDock';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { STARTED_WORK_EOD_EXCEEDED_EVENT, type StartedWorkEodExceededDetail } from '@/utils/virginEodSchedulingConflict';
import { describeDecisionRefusal, describeDisabledReason } from '@/utils/rolloverDecisionMessages';
import { EOD_NO_DECISION_RECORD_TEXT } from '@/lib/rollover/scheduleAlerts';
import { applyViewerOvertimeAuthority } from '../../../supabase/functions/_shared/rolloverDecisionPolicy';

export interface OpenRolloverDecision {
  id: string;
  origin_ticket_id: string;
  origin_date: string;
  kind: string;
  calc_version: string;
  detection: Record<string, unknown>;
  options_shown: Array<{ option: string; position: number; enabled: boolean; disabledReason: string | null; consequence: string; affects: string[] }> | null;
  catalyst_recommendation: Record<string, unknown> | null;
  acknowledged_at: string | null;
  requested_by: string | null;
  stated_remaining_min: number | null;
}

export const OPTION_LABELS: Record<string, string> = {
  reassign: 'Reassign to available capacity',
  approve_rollover: 'Approve rollover',
  authorize_overtime: 'Authorize overtime',
  reschedule: 'Reschedule',
  leave_unresolved: 'Leave unresolved',
};

/** W4b: tickets with an open server decision (as last read by the panel) — the legacy virgin modal stays closed for them (D-5). */
export const openRolloverDecisionTicketIds = new Set<string>();

/** OCT20A WS-ROLL2: the Dayboard strip asks the panel to open (navigation to the legitimate decisions; nothing is decided by the strip). */
export const OPEN_ROLLOVER_DECISIONS_EVENT = 'optibay:open-rollover-decisions';
/** OCT28 #9: the overruns the strip shows WITHOUT a decision record, so the panel names them instead of contradicting it. */
export interface OpenRolloverDecisionsDetail { withoutDecision?: Array<{ ticketId: string; label: string; text: string }> }

/** OCT28 #9: overruns with no decision record — each with its own words and its ticket (Reassign / OptiPlace live there). */
function WithoutDecisionList({ items }: { items: NonNullable<OpenRolloverDecisionsDetail['withoutDecision']> }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-1 text-sm" data-testid="rollover-decision-without-record">
      <p className="font-medium">Overruns with no decision to approve here ({items.length}):</p>
      <ul className="ml-3 list-disc space-y-1">
        {items.map((i) => (
          <li key={i.ticketId}>
            <a className="underline" href={`/?tab=dayboard&highlight=${encodeURIComponent(i.ticketId)}&openDetails=true`}
              data-testid={`rollover-decision-without-record-${i.ticketId}`}>{i.label}</a> — {i.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const DEFAULT_OPTION_ORDER = ['reassign', 'approve_rollover', 'authorize_overtime', 'reschedule', 'leave_unresolved'];

/** A decision about a day that has already ended (the server's stale-day sources), as opposed to today's boundary. */
export const STALE_DAY_DECISION_SOURCES: ReadonlySet<string> = new Set(['stale_chain_tail', 'stale_single_day']);
export function isStaleDayDecision(detection: Record<string, unknown> | null | undefined): boolean {
  return STALE_DAY_DECISION_SOURCES.has(String(detection?.source ?? ''));
}

/** Urgency for the banner: escalates as the authorized boundary approaches / passes (D-5). */
export function decisionUrgency(d: Pick<OpenRolloverDecision, 'detection'>, nowMs: number): 'notice' | 'warning' | 'urgent' {
  const boundary = Date.parse(String(d.detection?.boundary_at ?? ''));
  if (!Number.isFinite(boundary)) return 'warning';
  const minutesLeft = (boundary - nowMs) / 60_000;
  if (minutesLeft <= 30) return 'urgent';
  if (minutesLeft <= 120) return 'warning';
  return 'notice';
}

// The generated types do not know the W4 tables yet; one narrow cast for this read, the client is never detached.
type LooseDb = { from: (t: string) => any; rpc: (f: string, a: Record<string, unknown>) => any; functions: typeof supabase.functions };

export function RolloverDecisionPanel() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const db = supabase as unknown as LooseDb;
  const [mayDecide, setMayDecide] = useState(false);
  // B-W3: the server's answer for THIS person; null = unknown (read failed) → options shown as the server stored them.
  const [mayOvertime, setMayOvertime] = useState<boolean | null>(null);
  const [open, setOpen] = useState<OpenRolloverDecision[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [withoutDecision, setWithoutDecision] = useState<NonNullable<OpenRolloverDecisionsDetail['withoutDecision']>>([]);
  const [now, setNow] = useState(() => Date.now());
  const { begin } = useLatestResponseGuard();   // Issue #26: an older answer never replaces a newer one

  const load = useCallback(async () => {
    if (!shopId) return;
    const isLatest = begin();
    let data: unknown = null;
    try {
      ({ data } = await db.from('rollover_conversions')
      .select('id, origin_ticket_id, origin_date, kind, calc_version, detection, options_shown, catalyst_recommendation, acknowledged_at, requested_by, stated_remaining_min')
      .eq('shop_id', shopId).eq('state', 'proposed').order('detected_at', { ascending: true }).limit(50));
    } catch {
      data = null; // a failed READ keeps the last list; nothing is written
    }
    if (!isLatest()) return;
    if (data) {
      setOpen(data as OpenRolloverDecision[]);
      openRolloverDecisionTicketIds.clear();
      for (const d of data as OpenRolloverDecision[]) openRolloverDecisionTicketIds.add(d.origin_ticket_id);
    }
    setNow(Date.now());
  }, [shopId, db, begin]);

  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await db.rpc('can_i_decide_rollover', { p_shop: shopId });
        if (!cancelled) setMayDecide(data === true);
      } catch {
        if (!cancelled) setMayDecide(false); // unknown authority = no prompt (the server still decides every write)
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, db]);

  useEffect(() => {
    if (!shopId || !mayDecide) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await db.rpc('can_i_authorize_overtime', { p_shop: shopId });
        if (!cancelled) setMayOvertime(error ? null : data === true);
      } catch {
        if (!cancelled) setMayOvertime(null);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, mayDecide, db]);

  useEffect(() => {
    if (!mayDecide) return;
    void load();
    const t = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(t);
  }, [mayDecide, load]);
  // OCT20A WS-ROLL2: "Open end-of-day decisions" from the Scheduling Decision Required strip expands this panel.
  useEffect(() => {
    const onOpen = (e: Event) => {
      setWithoutDecision((e as CustomEvent<OpenRolloverDecisionsDetail | undefined>).detail?.withoutDecision ?? []);
      setExpanded(true); void load();
    };
    window.addEventListener(OPEN_ROLLOVER_DECISIONS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ROLLOVER_DECISIONS_EVENT, onOpen);
  }, [load]);
  // GAPS-2: also re-read on realtime rejoin / page return / back online — not only every 60 s.
  useScheduleSignalRefresh(() => { void load(); }, { enabled: mayDecide });

  const urgency = useMemo(() => {
    const levels = open.map((d) => decisionUrgency(d, now));
    return levels.includes('urgent') ? 'urgent' : levels.includes('warning') ? 'warning' : 'notice';
  }, [open, now]);

  if (!mayDecide) return null;
  // OCT28 #9: "Open end-of-day decisions" used to expand a panel that rendered nothing when no proposal was open (an
  // overrun alert can exist with no decision record — e.g. the head of an approved multi-day plan). Opened with
  // nothing to decide, the panel now SAYS so; it approves, extends or hides nothing.
  if (open.length === 0) {
    if (!expanded || typeof document === 'undefined') return null;
    return createPortal((
      <div role="region" aria-label="End-of-day decisions" data-testid="rollover-decision-panel-empty"
        className="pointer-events-auto order-2 w-full max-w-[30rem] lg:ml-auto rounded-lg border-2 shadow-lg border-sky-400 bg-sky-50 text-sky-900 dark:bg-sky-950 dark:text-sky-100">
        <div className="flex items-start justify-between gap-2 px-4 py-2">
          <div className="space-y-1 text-sm">
            <p className="font-medium">No end-of-day decision is waiting for approval.</p>
            <p>{EOD_NO_DECISION_RECORD_TEXT}</p>
            <WithoutDecisionList items={withoutDecision} />
          </div>
          <button type="button" className="text-xs underline" onClick={() => setExpanded(false)} data-testid="rollover-decision-empty-close">
            Close
          </button>
        </div>
      </div>
    ), getScheduleDock());
  }
  const tone = urgency === 'urgent' ? 'border-red-500 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100'
    : urgency === 'warning' ? 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100'
      : 'border-sky-400 bg-sky-50 text-sky-900 dark:bg-sky-950 dark:text-sky-100';

  // W4b: rendered into document.body. Mounted inside the Dashboard toolbar, `position: fixed` resolved against a
  // transformed ancestor: the panel anchored to the toolbar and grew upward behind the top bar, so the expanded
  // options were clipped and could not be clicked (found by the browser proof, 2026-10-17).
  if (typeof document === 'undefined') return null;
  return createPortal((
    <div role="region" aria-label="End-of-day decisions" data-testid="rollover-decision-panel"
      className={`pointer-events-auto order-2 w-full max-w-[30rem] lg:ml-auto rounded-lg border-2 shadow-lg ${tone}`}>
      <button type="button" className="flex w-full items-center justify-between px-4 py-2 text-left font-medium"
        onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} data-testid="rollover-decision-toggle">
        <span>{open.length} ticket{open.length === 1 ? '' : 's'} will not finish before the end of day — decision needed</span>
        <span aria-hidden>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="max-h-[32vh] lg:max-h-[60vh] space-y-3 overflow-y-auto px-4 pb-4">
          {open.map((d) => <DecisionCard key={`${d.id}:${d.calc_version}`} decision={d} onDone={load} mayAuthorizeOvertime={mayOvertime} />)}
          <WithoutDecisionList items={withoutDecision} />
        </div>
      )}
    </div>
  ), getScheduleDock()); // GAPS-6: shared dock (see scheduleDock.ts)
}

function DecisionCard({ decision, onDone, mayAuthorizeOvertime }: {
  decision: OpenRolloverDecision; onDone: () => Promise<void>; mayAuthorizeOvertime: boolean | null;
}) {
  const db = supabase as unknown as LooseDb;
  const [choice, setChoice] = useState<string | null>(null); // nothing preselected (D-4)
  const [note, setNote] = useState('');
  const [otMinutes, setOtMinutes] = useState('');
  const [busy, setBusy] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID()); // idempotency: one decision per opening
  const det = decision.detection ?? {};
  const options = applyViewerOvertimeAuthority((decision.options_shown && decision.options_shown.length > 0)
    ? [...decision.options_shown].sort((a, b) => a.position - b.position)
    : DEFAULT_OPTION_ORDER.map((option, i) => ({ option, position: i + 1, enabled: true, disabledReason: null as string | null, consequence: '', affects: [] as string[] })),
  mayAuthorizeOvertime);
  const rec = decision.catalyst_recommendation;

  const confirm = async () => {
    if (!choice) return;
    // W4b (D-7a): STARTED work is carried by the end-of-day carry flow (create-eod-rollover — approver-gated, records
    // its own approval, never stops the timer). The server refuses approving a started-work proposal here
    // (STARTED_WORK_CARRY_THROUGH_EOD_FLOW), so the approver is taken to that dialog instead.
    if (choice === 'approve_rollover' && decision.kind === 'started_eod') {
      // FLOYD 2026-10-26 (DEFECT 2): the boundary is an INSTANT and travels as one (`authorizedBoundaryAt`);
      // `techEOD` — display text — used to receive `String(det.boundary_at ?? '')` and was parsed as "HH:mm".
      const boundary = typeof det.boundary_at === 'string' && Number.isFinite(Date.parse(det.boundary_at))
        ? det.boundary_at : null;
      const detail: StartedWorkEodExceededDetail = {
        ticketId: decision.origin_ticket_id,
        customerName: `Ticket ${decision.origin_ticket_id}`,
        techName: '',
        techEOD: '',
        authorizedBoundaryAt: boundary,
        rolloverMinutes: Number(det.overrun_min ?? 0) || 0,
        trigger: 'rollover_decision_panel',
      };
      window.dispatchEvent(new CustomEvent(STARTED_WORK_EOD_EXCEEDED_EVENT, { detail }));
      return;
    }
    setBusy(true);
    try {
      if (choice === 'authorize_overtime') {
        const minutes = Number(otMinutes);
        const boundaryAt = String(det.boundary_at ?? '');
        if (!Number.isFinite(minutes) || minutes <= 0 || !boundaryAt || note.trim().length < 3) {
          toast({ title: 'Overtime needs minutes past the end of day and a reason', variant: 'destructive' });
          return;
        }
        const until = new Date(Date.parse(boundaryAt) + minutes * 60_000).toISOString();
        const { data: ot, error: otErr } = await db.rpc('authorize_ticket_overtime', {
          p_ticket: decision.origin_ticket_id, p_work_date: decision.origin_date, p_until: until, p_reason: note.trim(),
          p_request_id: requestId, p_boundary: boundaryAt, p_conversion: decision.id,
        });
        if (otErr || !['AUTHORIZED', 'REPLAY', 'ALREADY_AUTHORIZED'].includes(ot?.outcome)) {
          toast({ title: 'Overtime not authorized', description: describeDecisionRefusal(otErr?.message ?? ot?.cause), variant: 'destructive' });
          return;
        }
      }
      const { data, error } = await supabase.functions.invoke('rollover-decision', {
        body: { action: 'decide', conversionId: decision.id, requestId, decision: choice, calcVersion: decision.calc_version,
          reason: 'capacity', note: note.trim() || null },
      });
      if (error) { toast({ title: 'Decision not recorded', description: error.message, variant: 'destructive' }); return; }
      const outcome = data?.decision?.outcome ?? data?.data?.decision?.outcome;
      if (outcome === 'STALE_PROPOSAL') toast({ title: 'The schedule changed — review the updated numbers', variant: 'destructive' });
      else if (outcome === 'ALREADY_DECIDED') toast({ title: 'Someone already decided this one' });
      else if (outcome === 'REFUSED') toast({ title: 'Not allowed', description: describeDecisionRefusal(data?.decision?.cause), variant: 'destructive' });
      else toast({ title: `${OPTION_LABELS[choice]} recorded`,
        description: choice === 'reassign' || choice === 'reschedule' ? 'Now move the ticket from its card; nothing was moved automatically.' : undefined });
      await onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border bg-background p-3 text-foreground" data-testid="rollover-decision-card">
      <div className="text-sm font-semibold">Ticket {decision.origin_ticket_id}</div>
      {isStaleDayDecision(det) ? (
        // OCT20 WS-ROLL (FD-11): the last approved day of a multi-day repair — or of a single-day ticket
        // ('stale_single_day', FLOYD 2026-10-26) — already ended with the work unfinished. This decision is about
        // THAT day, not today: it is why "Authorize overtime" is unavailable while today's hours are open.
        <div className="text-xs text-muted-foreground" data-testid="rollover-decision-stale-tail">
          Rollover Decision Required · the last approved day ({String(det.last_approved_date ?? decision.origin_date)}) ended with
          the work unfinished · nothing was carried automatically
          {decision.requested_by ? ' · requested by the technician' : ''}
          {decision.stated_remaining_min != null ? ` · stated remaining ${decision.stated_remaining_min} min` : ''}
        </div>
      ) : (
      <div className="text-xs text-muted-foreground">
        {Number(det.overrun_min ?? 0) > 0 ? `${det.overrun_min} min past the end of day` : 'cannot start before the end of day'}
        {' · '}about {String(det.continuation_now_min ?? '?')} min would continue
        {det.worst_last_date ? ` (up to ${det.worst_continuation_min} min, latest ${det.worst_last_date})` : ''}
        {det.rollover_breaches_due_out ? ' · would break the due-out promise' : ''}
        {decision.requested_by ? ' · requested by the technician' : ''}
        {decision.stated_remaining_min != null ? ` · stated remaining ${decision.stated_remaining_min} min` : ''}
      </div>
      )}
      {rec && (
        <div className="mt-1 text-xs italic" data-testid="catalyst-recommendation">Catalyst recommendation (advisory only): {JSON.stringify(rec)}</div>
      )}
      <fieldset className="mt-2 space-y-1" disabled={busy}>
        <legend className="sr-only">Choose what happens</legend>
        {options.map((o) => (
          <label key={o.option} className={`flex items-start gap-2 text-sm ${o.enabled ? '' : 'opacity-50'}`}>
            <input type="radio" name={`rollover-${decision.id}`} value={o.option} checked={choice === o.option}
              disabled={!o.enabled} onChange={() => setChoice(o.option)} data-testid={`rollover-option-${o.option}`} />
            <span>
              <span className="font-medium">{o.position}. {OPTION_LABELS[o.option] ?? o.option}</span>
              {o.consequence ? <span className="block text-xs text-muted-foreground">{o.consequence}</span> : null}
              {!o.enabled && o.disabledReason ? <span className="block text-xs" data-testid={`rollover-option-${o.option}-unavailable`}>Not available: {describeDisabledReason(o.disabledReason)}{o.disabledReason === 'day_already_ended' ? ` (${String(det.last_approved_date ?? decision.origin_date)})` : ''}</span> : null}
              {o.affects?.length ? <span className="block text-xs">Affects: {o.affects.join(', ').replace(/_/g, ' ')}</span> : null}
            </span>
          </label>
        ))}
      </fieldset>
      {choice === 'authorize_overtime' && (
        <Input className="mt-2" inputMode="numeric" placeholder="Minutes past the end of day" value={otMinutes}
          onChange={(e) => setOtMinutes(e.target.value)} aria-label="Overtime minutes" />
      )}
      <Textarea className="mt-2" rows={2} maxLength={500} placeholder={choice === 'authorize_overtime' ? 'Reason (required)' : 'Note (optional)'}
        value={note} onChange={(e) => setNote(e.target.value)} aria-label="Decision note" />
      <div className="mt-2 flex justify-end">
        <Button size="sm" onClick={() => { void confirm(); }} disabled={!choice || busy} data-testid="rollover-decision-confirm">
          Confirm
        </Button>
      </div>
    </div>
  );
}
